import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, Typography,
    Button, Box, CircularProgress, Divider, IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import fileDownload from "js-file-download";
import { useTranslation } from "../../../hooks/useTranslation";

interface ExportDataModalProps {
    open: boolean;
    onClose: () => void;
}

const ExportDataModal: React.FC<ExportDataModalProps> = ({ open, onClose }) => {
    const { p } = useTranslation("export_data");
    const [loading, setLoading] = useState<string | null>(null);

    const handleDownload = async (endpoint: string, filename: string, key: string) => {
        setLoading(key);
        try {
            const response = await axios.get(endpoint, { responseType: "blob" });
            fileDownload(response.data, filename);
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            setLoading(null);
        }
    };

    const DownloadButton = ({ label, endpoint, filename, downloadKey }: {
        label: string; endpoint: string; filename: string; downloadKey: string;
    }) => (
        <Button
            variant="outlined"
            size="small"
            startIcon={loading === downloadKey ? <CircularProgress size={16} /> : <DownloadIcon />}
            disabled={loading !== null}
            onClick={() => handleDownload(endpoint, filename, downloadKey)}
            sx={{ mr: 1, mb: 1 }}
        >
            {label}
        </Button>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {p("title")}
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        {p("contacts")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {p("contacts_desc")}
                    </Typography>
                    <DownloadButton
                        label={p("download_csv")}
                        endpoint="/api/auth/export/contacts?format=csv"
                        filename="contacts.csv"
                        downloadKey="contacts-csv"
                    />
                    <DownloadButton
                        label={p("download_excel")}
                        endpoint="/api/auth/export/contacts?format=xlsx"
                        filename="contacts.xlsx"
                        downloadKey="contacts-xlsx"
                    />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        {p("saved_routes")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {p("saved_routes_desc")}
                    </Typography>
                    <DownloadButton
                        label={p("download_csv")}
                        endpoint="/api/auth/export/routes?format=csv"
                        filename="routes.csv"
                        downloadKey="routes-csv"
                    />
                    <DownloadButton
                        label={p("download_excel")}
                        endpoint="/api/auth/export/routes?format=xlsx"
                        filename="routes.xlsx"
                        downloadKey="routes-xlsx"
                    />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        {p("visit_reports")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {p("visit_reports_desc")}
                    </Typography>
                    <DownloadButton
                        label={p("download_excel")}
                        endpoint="/api/auth/export/visits?format=xlsx"
                        filename="visits.xlsx"
                        downloadKey="visits-xlsx"
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ExportDataModal;
